/**
 * useWallet — Supabase-backed wallet system
 * 
 * - Balance + transactions stored in `customers` table (wallet_balance, wallet_transactions columns)
 * - localStorage used as fast local cache & fallback
 * - All write operations sync to Supabase in background
 */

import { supabase } from '@/lib/supabase';

const WALLET_CACHE_PREFIX = 'clothybazar_wallet_cache';
const TXN_CACHE_PREFIX = 'clothybazar_txns_cache';

// ── User identity helpers ─────────────────────────────────────────────────────

function getLoggedInPhone(): string | null {
  try {
    const auth = localStorage.getItem('clothybazar_customer_auth');
    if (!auth) return null;
    const user = JSON.parse(auth);
    return user?.phone || user?.email || null;
  } catch {
    return null;
  }
}

function getCacheKey(userId: string): string {
  return `${WALLET_CACHE_PREFIX}_${userId}`;
}

function getTxnCacheKey(userId: string): string {
  return `${TXN_CACHE_PREFIX}_${userId}`;
}

// ── Cache helpers ─────────────────────────────────────────────────────────────

function readCachedBalance(userId: string): number {
  try {
    const raw = localStorage.getItem(getCacheKey(userId));
    return raw ? Math.max(0, Math.round(Number(raw))) : 0;
  } catch {
    return 0;
  }
}

function writeCachedBalance(userId: string, balance: number): void {
  try {
    localStorage.setItem(getCacheKey(userId), String(Math.max(0, Math.round(balance))));
  } catch { /* ignore */ }
}

function readCachedTxns(userId: string): WalletTransaction[] {
  try {
    const raw = localStorage.getItem(getTxnCacheKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeCachedTxns(userId: string, txns: WalletTransaction[]): void {
  try {
    localStorage.setItem(getTxnCacheKey(userId), JSON.stringify(txns));
  } catch { /* ignore */ }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WalletTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'credit' | 'debit';
}

// ── Core Supabase operations ───────────────────────────────────────────────────

async function fetchWalletFromSupabase(phone: string): Promise<{ balance: number; txns: WalletTransaction[] } | null> {
  const { data, error } = await supabase
    .from('customers')
    .select('wallet_balance, wallet_transactions')
    .eq('phone', phone)
    .maybeSingle();

  if (error || !data) return null;

  return {
    balance: typeof data.wallet_balance === 'number' ? data.wallet_balance : 0,
    txns: Array.isArray(data.wallet_transactions) ? (data.wallet_transactions as WalletTransaction[]) : [],
  };
}

async function saveWalletToSupabase(
  phone: string,
  balance: number,
  txns: WalletTransaction[]
): Promise<boolean> {
  const { error } = await supabase
    .from('customers')
    .update({
      wallet_balance: Math.max(0, Math.round(balance)),
      wallet_transactions: txns,
    })
    .eq('phone', phone);

  return !error;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Sync wallet from Supabase into local cache.
 * Call this on login or page load.
 */
export async function syncWalletFromSupabase(): Promise<{ balance: number; txns: WalletTransaction[] }> {
  const phone = getLoggedInPhone();
  if (!phone) return { balance: 0, txns: [] };

  const remote = await fetchWalletFromSupabase(phone);
  if (remote) {
    writeCachedBalance(phone, remote.balance);
    writeCachedTxns(phone, remote.txns);
    return remote;
  }
  // Fallback to cache
  return { balance: readCachedBalance(phone), txns: readCachedTxns(phone) };
}

/**
 * Get wallet balance — reads from local cache (fast, sync).
 * Always call syncWalletFromSupabase() first on mount for fresh data.
 */
export function getWalletBalance(): number {
  const phone = getLoggedInPhone();
  if (!phone) return 0;
  return readCachedBalance(phone);
}

/**
 * Get wallet transactions from cache (sync).
 */
export function getWalletTransactions(): WalletTransaction[] {
  const phone = getLoggedInPhone();
  if (!phone) return [];
  return readCachedTxns(phone);
}

/**
 * Deposit money into wallet.
 * Updates local cache immediately, syncs to Supabase in background.
 * Returns new balance.
 */
export async function depositToWallet(amount: number): Promise<number> {
  const phone = getLoggedInPhone();
  if (!phone) return 0;

  const current = readCachedBalance(phone);
  const newBalance = current + Math.max(0, Math.round(amount));
  const newTxn: WalletTransaction = {
    id: `DEP-${Date.now()}`,
    date: new Date().toISOString(),
    description: 'Money Added to Wallet',
    amount: Math.round(amount),
    type: 'credit',
  };
  const currentTxns = readCachedTxns(phone);
  const updatedTxns = [newTxn, ...currentTxns].slice(0, 100); // max 100 txns

  // Write cache immediately
  writeCachedBalance(phone, newBalance);
  writeCachedTxns(phone, updatedTxns);

  // Sync to Supabase
  await saveWalletToSupabase(phone, newBalance, updatedTxns);

  return newBalance;
}

/**
 * Update wallet after order:
 * - Deducts applied bonus
 * - Adds 10% cashback earned on paid amount
 * Syncs to Supabase in background.
 * Returns earned cashback amount.
 */
export async function updateWalletAfterOrder(
  paidAmount: number,
  appliedBonus: number,
  orderId?: string
): Promise<number> {
  const phone = getLoggedInPhone();
  if (!phone) {
    console.error('[Wallet] No logged in phone found!');
    return 0;
  }

  // Sync latest balance from Supabase first
  const remote = await fetchWalletFromSupabase(phone);
  const current = remote?.balance ?? readCachedBalance(phone);
  
  console.log('[Wallet] Before order:', { phone, currentBalance: current, appliedBonus, paidAmount });
  
  const earned = Math.floor(paidAmount * 0.1);
  const newBalance = Math.max(0, current - appliedBonus) + earned;
  
  console.log('[Wallet] After order:', { newBalance, earned, deducted: appliedBonus });

  const currentTxns = readCachedTxns(phone);
  const newTxns: WalletTransaction[] = [];

  if (appliedBonus > 0) {
    newTxns.push({
      id: `USE-${Date.now()}`,
      date: new Date().toISOString(),
      description: orderId ? `Wallet Used on Order ${orderId}` : 'Wallet Balance Used on Order',
      amount: appliedBonus,
      type: 'debit',
    });
  }

  if (earned > 0) {
    newTxns.push({
      id: `CASH-${Date.now() + 1}`,
      date: new Date().toISOString(),
      description: orderId ? `10% Cashback on Order ${orderId}` : '10% Cashback on Order',
      amount: earned,
      type: 'credit',
    });
  }

  const updatedTxns = [...newTxns, ...currentTxns].slice(0, 100);

  // Write cache immediately
  writeCachedBalance(phone, newBalance);
  writeCachedTxns(phone, updatedTxns);

  // Sync to Supabase
  await saveWalletToSupabase(phone, newBalance, updatedTxns);

  return earned;
}

/**
 * Refund wallet when order is cancelled/rejected by admin:
 * - Refunds wallet_discount (bonus that was used on this order)
 * - Reverses 10% cashback if it was already earned
 * Looks up customer by phone from Supabase directly.
 */
export async function refundWalletOnOrderCancel(
  customerPhone: string,
  walletDiscount: number,
  cashbackEarned: number,
  orderId?: string
): Promise<boolean> {
  console.log('[Wallet Refund] Starting refund:', { customerPhone, walletDiscount, cashbackEarned, orderId });
  
  if (!customerPhone) {
    console.error('[Wallet Refund] No customer phone provided!');
    return false;
  }

  // Normalize phone
  const phone = customerPhone.replace(/\D/g, '').replace(/^(91|0)/, '');
  if (!phone) {
    console.error('[Wallet Refund] Phone empty after normalization!');
    return false;
  }

  console.log('[Wallet Refund] Normalized phone:', phone);

  // Nothing to refund/reverse
  if (walletDiscount <= 0 && cashbackEarned <= 0) {
    console.log('[Wallet Refund] Nothing to refund or reverse');
    return true;
  }

  // Fetch latest wallet from Supabase
  const { data, error } = await supabase
    .from('customers')
    .select('wallet_balance, wallet_transactions')
    .eq('phone', phone)
    .maybeSingle();

  if (error) {
    console.error('[Wallet Refund] Supabase fetch error:', error);
    return false;
  }
  
  if (!data) {
    console.error('[Wallet Refund] Customer not found for phone:', phone);
    return false;
  }

  const current: number = typeof data.wallet_balance === 'number' ? data.wallet_balance : 0;
  const currentTxns: WalletTransaction[] = Array.isArray(data.wallet_transactions)
    ? (data.wallet_transactions as WalletTransaction[])
    : [];

  console.log('[Wallet Refund] Current balance:', current, 'Current txns count:', currentTxns.length);

  const newTxns: WalletTransaction[] = [];

  // 1. Refund the wallet bonus that was used
  let newBalance = current;
  if (walletDiscount > 0) {
    newBalance += walletDiscount;
    newTxns.push({
      id: `REFUND-${Date.now()}`,
      date: new Date().toISOString(),
      description: orderId
        ? `Refund — Order #${orderId.slice(-6).toUpperCase()} Cancelled`
        : 'Refund — Order Cancelled',
      amount: walletDiscount,
      type: 'credit',
    });
    console.log('[Wallet Refund] Adding refund credit:', walletDiscount);
  }

  // 2. Reverse cashback that was given (deduct it back)
  if (cashbackEarned > 0) {
    newBalance = Math.max(0, newBalance - cashbackEarned);
    newTxns.push({
      id: `REVOKE-${Date.now() + 1}`,
      date: new Date().toISOString(),
      description: orderId
        ? `Cashback Reversed — Order #${orderId.slice(-6).toUpperCase()} Cancelled`
        : 'Cashback Reversed — Order Cancelled',
      amount: cashbackEarned,
      type: 'debit',
    });
    console.log('[Wallet Refund] Adding cashback reversal debit:', cashbackEarned);
  }

  const updatedTxns = [...newTxns, ...currentTxns].slice(0, 100);

  console.log('[Wallet Refund] New balance:', newBalance, 'New txns count:', updatedTxns.length);

  const { error: saveError } = await supabase
    .from('customers')
    .update({
      wallet_balance: Math.max(0, Math.round(newBalance)),
      wallet_transactions: updatedTxns,
    })
    .eq('phone', phone);

  if (saveError) {
    console.error('[Wallet Refund] Supabase save error:', saveError);
    return false;
  }

  console.log('[Wallet Refund] Successfully completed!');
  return true;
}

/**
 * Max bonus that can be applied to an order total.
 */
export function calcApplicableBonus(walletBalance: number, orderTotal: number): number {
  return Math.min(walletBalance, orderTotal);
}

/**
 * Clear local wallet cache for the current user (on logout).
 */
export function clearWalletCache(): void {
  const phone = getLoggedInPhone();
  if (!phone) return;
  try {
    localStorage.removeItem(getCacheKey(phone));
    localStorage.removeItem(getTxnCacheKey(phone));
  } catch { /* ignore */ }
}

/**
 * Clear legacy wallet keys from old non-user-specific format.
 */
export function clearLegacyWalletData(): void {
  try {
    localStorage.removeItem('clothybazar_wallet');
    localStorage.removeItem('clothybazar_wallet_txns');
    // Remove old format keys
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key === 'clothybazar_wallet' || key === 'clothybazar_wallet_txns') {
        toRemove.push(key);
      }
    }
    toRemove.forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

/** @deprecated use syncWalletFromSupabase instead */
export function migrateOldWalletIfNeeded(): void {
  // disabled
}
